import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { apiFetch } from '../../utils/api';
import { useI18n } from '../../hooks/useI18n';

interface User {
    id: string;
    email: string;
    is_active: boolean;
    is_verified: boolean;
    is_superuser: boolean;
    cost_center_id?: string;
    language: string;
    cost_center?: {
        id: string;
        name: string;
    };
}

export default function SettingsPage() {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordChanging, setPasswordChanging] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const { t } = useI18n();

    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            setLoading(true);
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
            } else {
                setError('Failed to load user data');
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            setError('Failed to load user data');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError(t('settings.password.required', 'common'));
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError(t('settings.password.mismatch', 'common'));
            return;
        }

        try {
            setPasswordChanging(true);
            const response = await apiFetch('/api/auth/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            if (response.ok) {
                setPasswordSuccess(t('settings.password.success', 'common'));
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                const data = await response.json();
                setPasswordError(data.detail || t('settings.password.error', 'common'));
            }
        } catch {
            setPasswordError(t('settings.password.error', 'common'));
        } finally {
            setPasswordChanging(false);
        }
    };

    // Note: Language changes are handled directly by the LanguageSwitcher component
    // which updates the user preference via the useI18n hook

    return (
        <Layout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">{t('settings.title', 'common')}</h1>
                    <p className="text-muted-foreground">
                        {t('settings.description', 'common')}
                    </p>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}



                {loading ? (
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <Skeleton className="h-6 w-48" />
                                <Skeleton className="h-4 w-64" />
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Skeleton className="h-10 w-full" />
                                <Skeleton className="h-10 w-32" />
                            </CardContent>
                        </Card>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Account Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('settings.account.title', 'common')}</CardTitle>
                                <CardDescription>
                                    {t('settings.account.description', 'common')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t('settings.account.email', 'common')}
                                    </label>
                                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {user?.email}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t('settings.account.location', 'common')}
                                    </label>
                                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {user?.cost_center?.name || t('settings.account.noLocation', 'common')}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t('settings.account.role', 'common')}
                                    </label>
                                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {user?.is_superuser
                                            ? t('settings.account.administrator', 'common')
                                            : t('settings.account.user', 'common')
                                        }
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Language Preferences */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('settings.language.title', 'common')}</CardTitle>
                                <CardDescription>
                                    {t('settings.language.description', 'common')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <LanguageSwitcher
                                        className="max-w-xs"
                                        showLabel={true}
                                    />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {t('settings.language.autoSave', 'common')}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Change Password */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('settings.password.title', 'common')}</CardTitle>
                                <CardDescription>
                                    {t('settings.password.description', 'common')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handlePasswordChange} className="space-y-4">
                                    {passwordError && (
                                        <Alert variant="destructive">
                                            <AlertDescription>{passwordError}</AlertDescription>
                                        </Alert>
                                    )}
                                    {passwordSuccess && (
                                        <Alert>
                                            <AlertDescription>{passwordSuccess}</AlertDescription>
                                        </Alert>
                                    )}
                                    <div className="space-y-2">
                                        <Label htmlFor="currentPassword">
                                            {t('settings.password.currentPassword', 'common')}
                                        </Label>
                                        <Input
                                            id="currentPassword"
                                            type="password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className="max-w-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="newPassword">
                                            {t('settings.password.newPassword', 'common')}
                                        </Label>
                                        <Input
                                            id="newPassword"
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="max-w-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">
                                            {t('settings.password.confirmPassword', 'common')}
                                        </Label>
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="max-w-sm"
                                        />
                                    </div>
                                    <Button type="submit" disabled={passwordChanging}>
                                        {passwordChanging
                                            ? t('settings.password.changing', 'common')
                                            : t('settings.password.changeButton', 'common')}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
})
